// See [Barrel File Removal FAQ](https://hello.atlassian.net/wiki/x/KJT2aAE)
/* eslint-disable no-barrel-files/no-barrel-files */
import type {
	Maybe,
	SearchJiraBoardProductType,
	SearchJiraIssueProductType,
	SearchJiraProductType,
	SearchLinkedResult,
	SearchResult,
	SearchResultType,
} from '../../../../common/types';

import { type JiraStatusCategoryColorType } from './hydration/types';

/**
 * Hydrating the project avatar field causes additional latency so it is removed in this GraphQL Query
 * Investigation: https://hello.atlassian.net/wiki/x/iKKu8w
 */
const getSearchResultJiraProject = () => `
... on SearchResultJiraProject {
  projectId
  projectKey
  projectType
  lastModifiedDate
  project {
    webUrl
  }
	socialSignal(tenantId: $tenantId) @include(if: $includeSocialSignals) {
		signal
		timestamp
		user {
			accountId
			name
			picture
			... on AtlassianAccountUser {
				email
				extendedProfile {
					jobTitle
				}
			}
		}
	}
}`;

export interface SearchResultJiraProject extends SearchResult {
	projectId: string;
	projectKey: string;
	lastModifiedDate?: string;
	projectType: SearchJiraProductType;
	// Hydrated field
	project?: {
		webUrl?: string;
	};
	socialSignal: Maybe<{
		/** Should be either "comment" , "edit". */
		signal: Maybe<string>;
		timestamp: Maybe<string>;
		user: Maybe<{
			accountId: string;
			name: string;
			picture: string;
			email: Maybe<string>;
			extendedProfile: Maybe<{
				jobTitle: Maybe<string>;
			}>;
		}>;
	}>;
	__typename: 'SearchResultJiraProject';
}

export const isSearchResultJiraProject: (
	partial: SearchResult,
) => partial is SearchResultJiraProject = (
	partial: SearchResult,
): partial is SearchResultJiraProject => {
	return partial.__typename === 'SearchResultJiraProject';
};

/**
 * Hydrating issueTypeField and statusField have caused additional latency so it is removed in this GraphQL Query
 * Investigation: https://hello.atlassian.net/wiki/x/iKKu8w
 */
export const SearchResultJiraIssue = `
... on SearchResultJiraIssue {
  id
  title
  type
  description
  url
  iconUrl
  lastModifiedDate
  issueTypeId
  status {
    id
    name
  }
  issue {
    webUrl
		key
		assigneeField {
			user {
				name
				picture
			}
		}
    fieldsById(ids: ["project"]) {
      edges {
        node {
          __typename
          name
          ... on JiraProjectField {
            project {
              id
              name
			  projectType
            }
          }
        }
      }
    }
			issueTypeAvatarUrl
		issueTypeField @include(if: $includeJiraIssueType) {
			id
			issueType {
				id
				name
			}
		}
  }
  socialSignal(tenantId: $tenantId) @include(if: $includeSocialSignals) {
	    signal
		timestamp
		user {
			accountId
			name
			picture
			... on AtlassianAccountUser {
				email
				extendedProfile {
					jobTitle
				}
			}
	    }
  }
	linkedResults {
		id
		title
		url
		type
		description
		lastModifiedDate
		category
		subtype
		integrationId
		providerId
	}
  __typename
}
`;

export type JiraProjectField = {
	__typename: 'JiraProjectField';
	name: 'Project';
	project: {
		name: string;
		id: string;
		projectType?: SearchJiraIssueProductType;
	};
};

export type JiraIssueField = JiraProjectField;

export type SearchResultJiraIssue = {
	id: string;
	title: string;
	url: string;
	iconUrl?: Maybe<string>;
	type: SearchResultType;
	description: string;
	lastModifiedDate?: string;
	status?: {
		name?: string;
		colorName?: JiraStatusCategoryColorType;
	};
	// Hydrated field
	issue?: {
		webUrl?: string;
		key: string;
		fieldsById: {
			edges: {
				node: JiraProjectField;
			}[];
		};
		issueTypeAvatarUrl?: string;
		issueTypeField?: {
			id: string;
			issueType?: {
				id: string;
				name: string;
			};
		};
		assigneeField?: {
			user: Maybe<{
				name: string;
				picture: string;
			}>;
		};
	};
	socialSignal: Maybe<{
		/** Should be either "comment" , "edit", or "share". */
		signal: Maybe<string>;
		timestamp: Maybe<string>;
		user: Maybe<{
			accountId: string;
			name: string;
			picture: string;
			email: Maybe<string>;
			extendedProfile: Maybe<{
				jobTitle: Maybe<string>;
			}>;
		}>;
	}>;
	linkedResults?: Maybe<Array<SearchLinkedResult>>;
	__typename: 'SearchResultJiraIssue';
};

export const mapJiraIssuePartial: (partial: SearchResultJiraIssue) => {
	url: string;
	id: string;
	title: string;
	iconUrl?: Maybe<string>;
	type: SearchResultType;
	description: string;
	lastModifiedDate?: string;
	status?: {
		name?: string;
		colorName?: JiraStatusCategoryColorType;
	};
	issue?: {
		webUrl?: string;
		key: string;
		fieldsById: {
			edges: {
				node: JiraProjectField;
			}[];
		};
		issueTypeAvatarUrl?: string;
		issueTypeField?: {
			id: string;
			issueType?: {
				id: string;
				name: string;
			};
		};
		assigneeField?: {
			user: Maybe<{
				name: string;
				picture: string;
			}>;
		};
	};
	socialSignal: Maybe<{
		/** Should be either "comment" , "edit", or "share". */
		signal: Maybe<string>;
		timestamp: Maybe<string>;
		user: Maybe<{
			accountId: string;
			name: string;
			picture: string;
			email: Maybe<string>;
			extendedProfile: Maybe<{
				jobTitle: Maybe<string>;
			}>;
		}>;
	}>;
	linkedResults?: Maybe<Array<SearchLinkedResult>>;
	__typename: 'SearchResultJiraIssue';
} = (partial: SearchResultJiraIssue) => {
	return {
		...partial,
		url: partial.issue?.webUrl || partial.url,
	};
};

export const mapJiraProjectPartial: (
	partial: SearchResultJiraProject,
) => SearchResultJiraProject = (partial: SearchResultJiraProject): SearchResultJiraProject => ({
	...partial,
	url: partial.project?.webUrl || partial.url,
});

export const isSearchResultJiraIssue: (
	partial: SearchResult,
) => partial is SearchResultJiraIssue = (
	partial: SearchResult,
): partial is SearchResultJiraIssue => {
	return partial.__typename === 'SearchResultJiraIssue';
};

export const isJiraProjectField: (partial: JiraIssueField) => partial is JiraProjectField = (
	partial: JiraIssueField,
): partial is JiraProjectField => {
	return partial.__typename === 'JiraProjectField' && partial.name === 'Project';
};

export const SearchResultJiraFilter = ``;

export interface SearchResultJiraFilter extends SearchResult {
	type: SearchResultType;
	__typename: 'SearchResultJiraFilter';
}

export const isSearchResultJiraFilter: (
	partial: SearchResult,
) => partial is SearchResultJiraFilter = (
	partial: SearchResult,
): partial is SearchResultJiraFilter => {
	return partial.__typename === 'SearchResultJiraFilter';
};

export const SearchResultJiraDashboard = ``;

export interface SearchResultJiraDashboard extends SearchResult {
	type: SearchResultType;
	__typename: 'SearchResultJiraDashboard';
}

export const isSearchResultJiraDashboard: (
	partial: SearchResult,
) => partial is SearchResultJiraDashboard = (
	partial: SearchResult,
): partial is SearchResultJiraDashboard => {
	return partial.__typename === 'SearchResultJiraDashboard';
};

export const SearchResultJiraBoard = `
... on SearchResultJiraBoard {
  boardId
  container @include(if: $includeJiraBoardContainer) {
		__typename
		... on SearchResultJiraBoardProjectContainer {
			__typename
			projectName
		}
		... on SearchResultJiraBoardUserContainer {
			__typename
			userName
		}
	}
	board {
		boardFrontendUrl
		iconUrl
	}
  product
}
`;

export type SearchResultJiraBoardProjectContainer = {
	projectId: string;
	projectKey: string;
	projectName: string;
	projectTypeKey: SearchJiraProductType;
};

export type SearchResultJiraBoardUserContainer = {
	userId: string;
	userAccountId: string;
	userName: string;
};

export type SearchResultJiraBoardContainer =
	| SearchResultJiraBoardProjectContainer
	| SearchResultJiraBoardUserContainer;

export interface SearchResultJiraBoard extends SearchResult {
	boardId?: Maybe<string>;
	container?: Maybe<SearchResultJiraBoardContainer>;
	product: SearchJiraBoardProductType;
	__typename: 'SearchResultJiraBoard';
	board?: Maybe<{
		boardFrontendUrl: Maybe<string>;
		iconUrl: Maybe<string>;
	}>;
}

export const isSearchResultJiraBoard: (
	partial: SearchResult,
) => partial is SearchResultJiraBoard = (
	partial: SearchResult,
): partial is SearchResultJiraBoard => {
	return partial.__typename === 'SearchResultJiraBoard';
};

// TODO: Hydrate specific __typename for SearchResultJiraPlan
export interface SearchResultJiraPlan extends SearchResult {}

export const isSearchResultJiraPlan: (partial: SearchResult) => partial is SearchResultJiraPlan = (
	partial: SearchResult,
): partial is SearchResultJiraPlan => {
	return partial.__typename === 'SearchResultJiraPlan';
};

export type SearchJiraPartial =
	| SearchResultJiraProject
	| SearchResultJiraIssue
	| SearchResultJiraFilter
	| SearchResultJiraDashboard
	| SearchResultJiraBoard
	| SearchResultJiraPlan;

export const isSearchJiraPartial: (partial: SearchResult) => partial is SearchJiraPartial = (
	partial: SearchResult,
): partial is SearchJiraPartial => {
	return (
		isSearchResultJiraProject(partial) ||
		isSearchResultJiraIssue(partial) ||
		isSearchResultJiraFilter(partial) ||
		isSearchResultJiraDashboard(partial) ||
		isSearchResultJiraBoard(partial) ||
		isSearchResultJiraPlan(partial)
	);
};

export const getJiraPartials: () => string = () => {
	return `
		${getSearchResultJiraProject()}
		${SearchResultJiraIssue}
		${SearchResultJiraFilter}
		${SearchResultJiraDashboard}
		${SearchResultJiraBoard}
	`;
};

export {
	JiraStatusCategoryColor,
	type HydratedJiraIssue,
	type JiraHydrationData,
	type JiraStatusCategoryColorType,
} from './hydration/types';
export { fetchJiraFieldsQuery } from './hydration';
